import { Link, useLocation } from 'wouter';
import { useAuth } from '@/hooks/use-auth';
import { useSidebar } from '@/hooks/use-sidebar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  HomeIcon,
  Users,
  UserCheck,
  FileText,
  LogOut,
  Menu,
  FileSignature,
  Mail,
  Zap,
  Wrench,
  ChevronLeft,
  ChevronRight,
  MenuIcon,
  Cloud,
  KanbanSquare,
  Brain,
  FileImage,
  FileCheck,
  ClipboardList,
  TrendingUp,
  Calendar,
  Ticket,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export function NavLinks({
  onNavClick,
  collapsed = false,
}: {
  onNavClick?: () => void;
  collapsed?: boolean;
}) {
  const { logoutMutation, user } = useAuth();
  const [location] = useLocation();

  const handleNavLinkClick = () => {
    if (onNavClick) {
      onNavClick();
    }
  };

  const isActive = (path: string) => {
    // Handle homepage exact match
    if (path === '/' && location === '/') return true;
    // Handle other pages that start with path (preventing "/customers" matching "/custom")
    return path !== '/' && location.startsWith(path);
  };

  const activeClasses =
    'bg-slate-100 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 font-medium';

  // If collapsed, render icons only with tooltips
  if (collapsed) {
    return (
      <TooltipProvider delayDuration={300}>
        <div className="flex flex-col items-center">
          <div className="w-full">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 text-center">
              Menu
            </p>
            <div className="flex flex-col items-center">
              {/* Dashboard */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg ${isActive('/') ? activeClasses : ''}`}
                    >
                      <HomeIcon
                        className={`h-4 w-4 ${isActive('/') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Dashboard</TooltipContent>
              </Tooltip>

              {/* Customers */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/customers" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/customers') ? activeClasses : ''}`}
                    >
                      <Users
                        className={`h-4 w-4 ${isActive('/customers') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Customers</TooltipContent>
              </Tooltip>

              {/* Sales Pipeline (includes leads) */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/pipeline" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/pipeline') ? activeClasses : ''}`}
                    >
                      <UserCheck
                        className={`h-4 w-4 ${isActive('/pipeline') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Pipeline</TooltipContent>
              </Tooltip>

              {/* Proposals */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/proposals" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/proposals') ? activeClasses : ''}`}
                    >
                      <FileText
                        className={`h-4 w-4 ${isActive('/proposals') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Proposals</TooltipContent>
              </Tooltip>

              {/* Letters of Authority */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/loa" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/loa') ? activeClasses : ''}`}
                    >
                      <FileSignature
                        className={`h-4 w-4 ${isActive('/loa') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Letters of Authority</TooltipContent>
              </Tooltip>

              {/* Installations */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/installations" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/installations') ? activeClasses : ''}`}
                    >
                      <Wrench
                        className={`h-4 w-4 ${isActive('/installations') ? 'text-teal-500 dark:text-teal-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Installations</TooltipContent>
              </Tooltip>

              {/* Site Surveys */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/site-surveys" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/site-surveys') ? activeClasses : ''}`}
                    >
                      <FileImage
                        className={`h-4 w-4 ${isActive('/site-surveys') ? 'text-blue-500 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Site Surveys</TooltipContent>
              </Tooltip>

              {/* HIES Completion */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/hies-completion" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/hies-completion') ? activeClasses : ''}`}
                    >
                      <FileCheck
                        className={`h-4 w-4 ${isActive('/hies-completion') ? 'text-green-500 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">HIES Completion</TooltipContent>
              </Tooltip>

              {/* Survey Management */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/survey-management" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/survey-management') ? activeClasses : ''}`}
                    >
                      <ClipboardList
                        className={`h-4 w-4 ${isActive('/survey-management') ? 'text-green-500 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Survey Management</TooltipContent>
              </Tooltip>

              {/* Scheduling */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/scheduling" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/scheduling') ? activeClasses : ''}`}
                    >
                      <Calendar
                        className={`h-4 w-4 ${isActive('/scheduling') ? 'text-blue-500 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Scheduling</TooltipContent>
              </Tooltip>

              {/* Support Tickets */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/support-tickets" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/support-tickets') ? activeClasses : ''}`}
                    >
                      <Ticket
                        className={`h-4 w-4 ${isActive('/support-tickets') ? 'text-purple-500 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Support Tickets</TooltipContent>
              </Tooltip>

              {/* Analytics */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/analytics" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/analytics') ? activeClasses : ''}`}
                      data-testid="button-analytics"
                    >
                      <TrendingUp
                        className={`h-4 w-4 ${isActive('/analytics') ? 'text-orange-500 dark:text-orange-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Analytics</TooltipContent>
              </Tooltip>

              {/* AI Agent */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link href="/ai-agent" onClick={handleNavLinkClick}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`w-8 h-8 rounded-lg mt-2 ${isActive('/ai-agent') ? activeClasses : ''}`}
                    >
                      <Brain
                        className={`h-4 w-4 ${isActive('/ai-agent') ? 'text-blue-500 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">AI Agent</TooltipContent>
              </Tooltip>
            </div>
          </div>

          <div className="h-px bg-slate-200 dark:bg-slate-700/60 my-3 w-8"></div>

          {/* API Integrations */}
          <div className="w-full flex flex-col items-center">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 text-center">
              API
            </p>

            {/* OpenSolar Settings */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/opensolar/settings" onClick={handleNavLinkClick}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`w-8 h-8 rounded-lg mb-2 ${isActive('/opensolar/settings') ? activeClasses : ''}`}
                  >
                    <Cloud
                      className={`h-4 w-4 ${isActive('/opensolar/settings') ? 'text-teal-500 dark:text-teal-400' : 'text-slate-500 dark:text-slate-400'}`}
                    />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">OpenSolar API</TooltipContent>
            </Tooltip>

            {/* Meta Conversions Settings */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/settings/meta-conversions" onClick={handleNavLinkClick}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`w-8 h-8 rounded-lg mb-2 ${isActive('/settings/meta-conversions') ? activeClasses : ''}`}
                  >
                    <Zap
                      className={`h-4 w-4 ${isActive('/settings/meta-conversions') ? 'text-teal-500 dark:text-teal-400' : 'text-slate-500 dark:text-slate-400'}`}
                    />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">Meta Conversions API</TooltipContent>
            </Tooltip>

            {/* Email Templates */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/email-templates" onClick={handleNavLinkClick}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`w-8 h-8 rounded-lg mb-2 ${isActive('/email-templates') ? activeClasses : ''}`}
                  >
                    <Mail
                      className={`h-4 w-4 ${isActive('/email-templates') ? 'text-teal-500 dark:text-teal-400' : 'text-slate-500 dark:text-slate-400'}`}
                    />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">Email Templates</TooltipContent>
            </Tooltip>

            {/* Logout */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="w-10 h-10 rounded-lg mt-auto border border-slate-200 dark:border-slate-700 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800/30 transition-colors"
                  onClick={() => {
                    // Close any mobile menu if we're on mobile
                    if (onNavClick) onNavClick();

                    // Use a try-catch to handle potential logout errors
                    try {
                      logoutMutation.mutate();
                      console.log('Logout mutation triggered');
                    } catch (error) {
                      console.error('Error triggering logout:', error);
                    }
                  }}
                  disabled={logoutMutation.isPending}
                >
                  {logoutMutation.isPending ? (
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <LogOut className="h-5 w-5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Logout</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Full sidebar view
  return (
    <div className="flex flex-col gap-1.5">
      <div className="mb-1">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider pl-3 mb-2">
          Main
        </p>
        <Link href="/" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/') ? activeClasses : ''}`}
          >
            <HomeIcon
              className={`mr-2 h-4 w-4 ${isActive('/') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Dashboard
          </Button>
        </Link>
        <Link href="/customers" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/customers') ? activeClasses : ''}`}
          >
            <Users
              className={`mr-2 h-4 w-4 ${isActive('/customers') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Customers
          </Button>
        </Link>
        <Link href="/pipeline" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/pipeline') ? activeClasses : ''}`}
          >
            <UserCheck
              className={`mr-2 h-4 w-4 ${isActive('/pipeline') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Pipeline
          </Button>
        </Link>
        <Link href="/proposals" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/proposals') ? activeClasses : ''}`}
          >
            <FileText
              className={`mr-2 h-4 w-4 ${isActive('/proposals') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Proposals
          </Button>
        </Link>
        <Link href="/loa" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/loa') ? activeClasses : ''}`}
          >
            <FileSignature
              className={`mr-2 h-4 w-4 ${isActive('/loa') ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Letters of Authority
          </Button>
        </Link>
        <Link href="/installations" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/installations') ? activeClasses : ''}`}
          >
            <Wrench
              className={`mr-2 h-4 w-4 ${isActive('/installations') ? 'text-teal-500 dark:text-teal-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Installations
          </Button>
        </Link>
        <Link href="/site-surveys" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/site-surveys') ? activeClasses : ''}`}
          >
            <FileImage
              className={`mr-2 h-4 w-4 ${isActive('/site-surveys') ? 'text-blue-500 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Site Surveys
          </Button>
        </Link>
        <Link href="/hies-completion" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/hies-completion') ? activeClasses : ''}`}
          >
            <FileCheck
              className={`mr-2 h-4 w-4 ${isActive('/hies-completion') ? 'text-green-500 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            HIES Completion
          </Button>
        </Link>
        <Link href="/survey-management" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/survey-management') ? activeClasses : ''}`}
          >
            <ClipboardList
              className={`mr-2 h-4 w-4 ${isActive('/survey-management') ? 'text-green-500 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Survey Management
          </Button>
        </Link>
        <Link href="/scheduling" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/scheduling') ? activeClasses : ''}`}
          >
            <Calendar
              className={`mr-2 h-4 w-4 ${isActive('/scheduling') ? 'text-blue-500 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Scheduling
          </Button>
        </Link>
        <Link href="/support-tickets" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/support-tickets') ? activeClasses : ''}`}
          >
            <Ticket
              className={`mr-2 h-4 w-4 ${isActive('/support-tickets') ? 'text-purple-500 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Support Tickets
          </Button>
        </Link>
        <Link href="/analytics" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/analytics') ? activeClasses : ''}`}
            data-testid="link-analytics"
          >
            <TrendingUp
              className={`mr-2 h-4 w-4 ${isActive('/analytics') ? 'text-orange-500 dark:text-orange-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Analytics
          </Button>
        </Link>
        <Link href="/ai-agent" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/ai-agent') ? activeClasses : ''}`}
          >
            <Brain
              className={`mr-2 h-4 w-4 ${isActive('/ai-agent') ? 'text-blue-500 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            AI Agent
          </Button>
        </Link>
      </div>

      <div className="h-px bg-slate-200 dark:bg-slate-700/60 my-3"></div>

      {/* API Integrations section */}
      <div>
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider pl-3 mb-2">
          Integrations
        </p>
        <Link href="/opensolar/settings" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/opensolar/settings') ? activeClasses : ''}`}
          >
            <Cloud
              className={`mr-2 h-4 w-4 ${isActive('/opensolar/settings') ? 'text-teal-500 dark:text-teal-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            OpenSolar API
          </Button>
        </Link>
        <Link href="/settings/meta-conversions" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/settings/meta-conversions') ? activeClasses : ''}`}
          >
            <Zap
              className={`mr-2 h-4 w-4 ${isActive('/settings/meta-conversions') ? 'text-teal-500 dark:text-teal-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Meta Conversions API
          </Button>
        </Link>
        <Link href="/email-templates" onClick={handleNavLinkClick}>
          <Button
            variant="ghost"
            className={`w-full justify-start btn-animated text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 ${isActive('/email-templates') ? activeClasses : ''}`}
          >
            <Mail
              className={`mr-2 h-4 w-4 ${isActive('/email-templates') ? 'text-teal-500 dark:text-teal-400' : 'text-slate-500 dark:text-slate-400'}`}
            />
            Email Templates
          </Button>
        </Link>
      </div>

      <div className="mt-auto pt-6">
        {user && (
          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-8 w-8 rounded-full bg-indigo-500 dark:bg-indigo-600 flex items-center justify-center text-white text-sm font-bold">
                {user.username.charAt(0).toUpperCase()}
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  {user.username}
                </span>
                <span className="text-xs text-indigo-600 dark:text-indigo-400">Administrator</span>
              </div>
            </div>
          </div>
        )}
        <Button
          variant="outline"
          className="w-full justify-start border border-slate-200 dark:border-slate-700 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800/30 transition-colors"
          onClick={() => {
            // Close any mobile menu if we're on mobile
            if (onNavClick) onNavClick();

            // Use a try-catch to handle potential logout errors
            try {
              logoutMutation.mutate();
              console.log('Logout mutation triggered');
            } catch (error) {
              console.error('Error triggering logout:', error);
            }
          }}
          disabled={logoutMutation.isPending}
        >
          {logoutMutation.isPending ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Logging out...
            </>
          ) : (
            <>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default function NavSidebar() {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const { collapsed, toggleSidebar } = useSidebar();

  // Mobile navigation sheet
  const mobileNav = (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden fixed top-4 left-4 z-50 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-sm border border-slate-200 dark:border-slate-700"
        >
          <Menu className="h-5 w-5 text-slate-700 dark:text-slate-300" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-[280px] p-4 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
      >
        <div className="flex justify-center mb-6 fade-in">
          <div className="flex-shrink-0">
            <img
              src="/images/domelec-logo-new-official.png"
              alt="DomElec Logo"
              className="h-12 w-auto"
              style={{ maxWidth: '160px', objectFit: 'contain' }}
            />
          </div>
        </div>
        <ScrollArea className="h-[calc(100vh-10rem)]">
          <div className="slide-up">
            <NavLinks onNavClick={() => setOpen(false)} />
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );

  // Desktop sidebar - expanded
  const desktopNavExpanded = (
    <div className="nav-sidebar hidden md:flex bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 shadow-sm sticky top-0 h-screen">
      <div className="flex flex-col w-full overflow-hidden">
        <div className="flex justify-between items-center p-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
          <div className="flex-shrink-0 flex items-center">
            <img
              src="/images/domelec-logo-new-official.png"
              alt="DomElec Logo"
              className="h-9 w-auto"
              style={{ maxWidth: '150px', objectFit: 'contain' }}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 flex-shrink-0 ml-2"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        </div>
        <ScrollArea className="h-[calc(100vh-4rem)] px-3 w-full">
          <div className="py-3">
            <NavLinks />
          </div>
        </ScrollArea>
      </div>
    </div>
  );

  // Desktop sidebar - collapsed
  const desktopNavCollapsed = (
    <div className="nav-sidebar collapsed hidden md:flex bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 shadow-sm flex-col sticky top-0 h-screen">
      <div className="flex flex-col w-full overflow-hidden">
        <div className="flex justify-center items-center p-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
        <ScrollArea className="h-[calc(100vh-4rem)] w-full">
          <div className="py-3 flex flex-col items-center">
            <NavLinks collapsed={true} />
          </div>
        </ScrollArea>
      </div>
    </div>
  );

  return <>{isMobile ? mobileNav : collapsed ? desktopNavCollapsed : desktopNavExpanded}</>;
}
